// src/components/admin/ConfigManagement.js
import React, { useState, useEffect, useCallback, memo } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import ColorPicker from "./shared/ColorPicker";
import {
  Save,
  DollarSign as Money,
  Loader2,
  AlertCircle,
  Check,
  Globe,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Palette as Color,
  Layout,
  Type,
  Settings,
  Brush,
  Palette,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Memoized individual form components to prevent unnecessary re-renders
const TextField = memo(
  ({
    label,
    name,
    value,
    onChange,
    placeholder,
    className,
    type = "text",
    required = false,
    helperText = null,
  }) => {
    // Create local state to avoid re-renders in parent component
    const [localValue, setLocalValue] = useState(value || "");

    // Update local state when prop changes
    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      // Only update parent state when field loses focus
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <input
          id={name}
          name={name}
          type={type}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          className={`w-full p-2 border border-gray-300 rounded-md ${
            className || ""
          }`}
        />
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const IconTextField = memo(
  ({
    label,
    name,
    value,
    onChange,
    icon: Icon,
    className,
    placeholder,
    required = false,
    helperText = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id={name}
            name={name}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            className={`w-full p-2 pl-10 border border-gray-300 rounded-md ${
              className || ""
            }`}
          />
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const TextArea = memo(
  ({
    label,
    name,
    value,
    onChange,
    rows = 3,
    className,
    required = false,
    helperText = null,
    icon: Icon = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <div className={Icon ? "relative" : ""}>
          {Icon && (
            <div className="absolute top-3 left-3 pointer-events-none">
              <Icon className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <textarea
            id={name}
            name={name}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={rows}
            required={required}
            className={`w-full p-2 ${
              Icon ? "pl-10" : ""
            } border border-gray-300 rounded-md ${className || ""}`}
          ></textarea>
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const SelectField = memo(
  ({
    label,
    name,
    value,
    onChange,
    options,
    className,
    required = false,
    helperText = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      // For select fields, we can trigger onChange immediately since there's no typing
      onChange(name, newValue);
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <select
          id={name}
          name={name}
          value={localValue}
          onChange={handleChange}
          required={required}
          className={`w-full p-2 border border-gray-300 rounded-md ${
            className || ""
          }`}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const Checkbox = memo(
  ({ label, name, checked, onChange, helperText = null }) => {
    const handleChange = (e) => {
      onChange(name, e.target.checked);
    };

    return (
      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={checked || false}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            {label}
          </span>
        </label>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1 ml-6">{helperText}</p>
        )}
      </div>
    );
  }
);

const CurrencyField = memo(
  ({
    label,
    name,
    value,
    onChange,
    min = "0.01",
    step = "0.01",
    className,
    required = false,
    helperText = null,
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
    };

    const handleBlur = () => {
      if (localValue !== value) {
        onChange(name, localValue);
      }
    };

    return (
      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor={name}
        >
          {label} {required && "*"}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500">$</span>
          </div>
          <input
            id={name}
            name={name}
            type="number"
            min={min}
            step={step}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            required={required}
            className={`w-full p-2 pl-8 border border-gray-300 rounded-md ${
              className || ""
            }`}
          />
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

const ColorField = memo(({ label, colorName, color, onChange }) => {
  // Handle color change and pass it to parent
  const handleColorChange = useCallback(
    (newColor) => {
      onChange(colorName, newColor);
    },
    [onChange, colorName]
  );

  return (
    <div className="flex items-center mb-4">
      <label className="block text-sm font-medium text-gray-700 w-32">
        {label}
      </label>
      <div className="flex-grow">
        <ColorPicker
          color={color || "#ffffff"}
          onChange={handleColorChange}
          allowAlpha={false}
        />
      </div>
    </div>
  );
});

// Configuration section component
const ConfigSection = memo(
  ({ title, icon: Icon, id, isExpanded, onToggle, children }) => (
    <div className="mb-6 bg-white rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={() => onToggle(id)}
        className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 text-left"
        type="button"
      >
        <div className="flex items-center">
          {Icon && <Icon className="w-5 h-5 text-blue-600 mr-2" />}
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 border-t border-gray-200">{children}</div>
      )}
    </div>
  )
);

// Main component
const ConfigManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedSection, setExpandedSection] = useState("general");

  // Configuration state
  const [config, setConfig] = useState({
    // General Settings
    siteName: "",
    siteTagline: "",
    siteDescription: "",
    siteKeywords: "",
    logoUrl: "",
    faviconUrl: "",

    // Pricing information
    onlyPricingTier: false,
    businessOrderCount: "",
    businessUserCharging: false,
    businessUserChargingAmount: "",
    businessWholesalePricing: false,

    // Contact Information
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",

    // Social Media
    socialLinks: {
      facebook: "",
      twitter: "",
      instagram: "",
      linkedin: "",
      youtube: "",
    },

    // Colors & Branding
    colors: {
      primary: "#3B82F6",
      secondary: "#10B981",
      accent: "#8B5CF6",
      inputBorder: "#eae7e1",
      textPrimary: "#111827",
      textSecondary: "#6B7280",
      background: "#FFFFFF",
      backgroundAlt: "#F3F4F6",
    },

    // Typography
    typography: {
      headingFont: "Inter",
      bodyFont: "Inter",
      baseSize: "16px",
    },

    // SEO & Meta
    metaTitle: "",
    metaDescription: "",
    googleAnalyticsId: "",

    // Features Toggles
    features: {
      enableBlog: true,
      enableNewsletter: true,
      enableSearch: true,
      enableDarkMode: false,
    },

    // API Keys & Integrations
    apiKeys: {
      googleMaps: "",
      mailchimp: "",
      stripe: "",
    },
  });

  // Check admin status
  useEffect(() => {
    if (!user || !user.isAdmin) {
      setError("You do not have permission to access this section");
      setLoading(false);
    }
  }, [user]);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/config", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setConfig((prevConfig) => ({
          ...prevConfig,
          ...response.data,
        }));
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching configuration:", err);
      setError("Failed to load configuration. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Field change handlers that avoid unnecessary re-renders
  const handleFieldChange = useCallback((fieldName, value) => {
    setConfig((prev) => {
      if (fieldName.includes(".")) {
        const [section, property] = fieldName.split(".");
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [property]: value,
          },
        };
      } else {
        return {
          ...prev,
          [fieldName]: value,
        };
      }
    });
  }, []);

  // Specialized handler for color changes
  const handleColorChange = useCallback((colorName, value) => {
    setConfig((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorName]: value,
      },
    }));
  }, []);

  // Save configuration
  const saveConfig = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.post("/api/config", config, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess("Configuration saved successfully!");
      setError(null);

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error("Error saving configuration:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSection((prevExpanded) =>
      prevExpanded === section ? null : section
    );
  }, []);

  // Clear notification messages
  const clearNotifications = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold">Website Configuration</h2>

      <div className="bg-gray-50 p-6 rounded-2xl">
        {/* Notification area */}
        <div className="container mx-auto px-4 py-2">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <p>{error}</p>
              </div>
              <button
                onClick={clearNotifications}
                className="text-red-500 hover:text-red-700"
                type="button"
              >
                ×
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
              <div className="flex items-center">
                <Check className="w-5 h-5 mr-2" />
                <p>{success}</p>
              </div>
              <button
                onClick={clearNotifications}
                className="text-green-500 hover:text-green-700"
                type="button"
              >
                ×
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="h-64 flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={saveConfig} className="space-y-4">
            {/* Form header */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                <h1 className="text-2xl font-semibold text-gray-800">
                  Website Configuration
                </h1>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save All Settings
                    </>
                  )}
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Manage your website's settings, appearance, and behavior.
                Changes will be applied site-wide.
              </p>
            </div>

            {/* Configuration Sections */}
            <div className="grid grid-cols-1 gap-6">
              {/* General Settings */}
              <ConfigSection
                title="General Settings"
                icon={Globe}
                id="general"
                isExpanded={expandedSection === "general"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Website Name"
                      name="siteName"
                      value={config.siteName}
                      onChange={handleFieldChange}
                      required={true}
                      helperText="This will appear in the browser tab and across the site"
                    />

                    <TextField
                      label="Tagline"
                      name="siteTagline"
                      value={config.siteTagline}
                      onChange={handleFieldChange}
                      helperText="A short phrase describing your website"
                    />

                    <TextArea
                      label="Site Description"
                      name="siteDescription"
                      value={config.siteDescription}
                      onChange={handleFieldChange}
                      helperText="A longer description of your website (used for SEO)"
                    />
                  </div>

                  <div className="space-y-4">
                    <TextField
                      label="Site Keywords"
                      name="siteKeywords"
                      value={config.siteKeywords}
                      onChange={handleFieldChange}
                      helperText="Comma-separated keywords for SEO"
                    />

                    <TextField
                      label="Logo URL"
                      name="logoUrl"
                      value={config.logoUrl}
                      onChange={handleFieldChange}
                      helperText="URL to your logo image (use Media Manager to upload)"
                    />

                    <TextField
                      label="Favicon URL"
                      name="faviconUrl"
                      value={config.faviconUrl}
                      onChange={handleFieldChange}
                      helperText="URL to your favicon image (should be a square .ico or .png)"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Pricing Information */}
              <ConfigSection
                title="Pricing Information"
                icon={Money}
                id="pricing"
                isExpanded={expandedSection === "pricing"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="mb-4">
                    <Checkbox
                      label="Only Show Pricing Tiers In Users' Cart"
                      name="onlyPricingTier"
                      checked={config.onlyPricingTier}
                      onChange={handleFieldChange}
                      helperText="By Selecting, Only pricing tiers will be shown"
                    />
                  </div>

                  <div className="mb-4">
                    <TextField
                      label="Business Order Count"
                      name="businessOrderCount"
                      value={config.businessOrderCount}
                      onChange={handleFieldChange}
                      type="number"
                    />
                  </div>

                  <div className="mb-4">
                    <Checkbox
                      label="Charge Business Users for Account Verification"
                      name="businessUserCharging"
                      checked={config.businessUserCharging}
                      onChange={handleFieldChange}
                      helperText="If enabled, business users will be charged for account verification (refundable)"
                    />
                  </div>

                  {config.businessUserCharging && (
                    <div className="mb-4">
                      <CurrencyField
                        label="Business Verification Amount"
                        name="businessUserChargingAmount"
                        value={config.businessUserChargingAmount}
                        onChange={handleFieldChange}
                        helperText="This amount will be charged to business users for verification (refundable)"
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <Checkbox
                      label="Enable Wholesale Pricing for Business Accounts"
                      name="businessWholesalePricing"
                      checked={config.businessWholesalePricing}
                      onChange={handleFieldChange}
                      helperText="If enabled, business users will see wholesale prices in cart regardless of quantity"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Contact Information */}
              <ConfigSection
                title="Contact Information"
                icon={Mail}
                id="contact"
                isExpanded={expandedSection === "contact"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <IconTextField
                      label="Contact Email"
                      name="contactEmail"
                      value={config.contactEmail}
                      onChange={handleFieldChange}
                      icon={Mail}
                      type="email"
                    />

                    <IconTextField
                      label="Contact Phone"
                      name="contactPhone"
                      value={config.contactPhone}
                      onChange={handleFieldChange}
                      icon={Phone}
                    />
                  </div>

                  <div className="space-y-4">
                    <TextArea
                      label="Contact Address"
                      name="contactAddress"
                      value={config.contactAddress}
                      onChange={handleFieldChange}
                      icon={MapPin}
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Social Media */}
              <ConfigSection
                title="Social Media"
                icon={Facebook}
                id="social"
                isExpanded={expandedSection === "social"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <IconTextField
                      label="Facebook"
                      name="socialLinks.facebook"
                      value={config.socialLinks?.facebook}
                      onChange={handleFieldChange}
                      icon={Facebook}
                      placeholder="https://facebook.com/yourpage"
                    />

                    <IconTextField
                      label="Twitter"
                      name="socialLinks.twitter"
                      value={config.socialLinks?.twitter}
                      onChange={handleFieldChange}
                      icon={Twitter}
                      placeholder="https://twitter.com/yourhandle"
                    />

                    <IconTextField
                      label="Instagram"
                      name="socialLinks.instagram"
                      value={config.socialLinks?.instagram}
                      onChange={handleFieldChange}
                      icon={Instagram}
                      placeholder="https://instagram.com/yourhandle"
                    />
                  </div>

                  <div className="space-y-4">
                    <IconTextField
                      label="LinkedIn"
                      name="socialLinks.linkedin"
                      value={config.socialLinks?.linkedin}
                      onChange={handleFieldChange}
                      icon={Linkedin}
                      placeholder="https://linkedin.com/company/yourcompany"
                    />

                    <IconTextField
                      label="YouTube"
                      name="socialLinks.youtube"
                      value={config.socialLinks?.youtube}
                      onChange={handleFieldChange}
                      icon={Youtube}
                      placeholder="https://youtube.com/c/yourchannel"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Colors & Branding */}
              <ConfigSection
                title="Colors & Branding"
                icon={Palette}
                id="colors"
                isExpanded={expandedSection === "colors"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Brand Colors</h4>

                    <ColorField
                      label="Primary"
                      colorName="primary"
                      color={config.colors?.primary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Secondary"
                      colorName="secondary"
                      color={config.colors?.secondary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Accent"
                      colorName="accent"
                      color={config.colors?.accent}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Border"
                      colorName="inputBorder"
                      color={config.colors?.inputBorder}
                      onChange={handleColorChange}
                    />
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">
                      Text & Background Colors
                    </h4>

                    <ColorField
                      label="Text Primary"
                      colorName="textPrimary"
                      color={config.colors?.textPrimary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Text Secondary"
                      colorName="textSecondary"
                      color={config.colors?.textSecondary}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Background"
                      colorName="background"
                      color={config.colors?.background}
                      onChange={handleColorChange}
                    />

                    <ColorField
                      label="Background Alt"
                      colorName="backgroundAlt"
                      color={config.colors?.backgroundAlt}
                      onChange={handleColorChange}
                    />
                  </div>
                </div>

                {/* Preview section for colors */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Color Preview
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.primary }}
                    >
                      Primary
                    </div>
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.secondary }}
                    >
                      Secondary
                    </div>
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.accent }}
                    >
                      Accent
                    </div>
                    <div
                      className="p-4 rounded-md text-white flex items-center justify-center h-20"
                      style={{ backgroundColor: config.colors?.inputBorder }}
                    >
                      Border
                    </div>
                    <div
                      className="p-4 rounded-md flex items-center justify-center h-20"
                      style={{
                        backgroundColor: config.colors?.background,
                        color: config.colors?.textPrimary,
                      }}
                    >
                      Background / Text
                    </div>
                    <div
                      className="p-4 rounded-md flex items-center justify-center h-20"
                      style={{
                        backgroundColor: config.colors?.backgroundAlt,
                        color: config.colors?.textSecondary,
                      }}
                    >
                      Background Alt / Text Secondary
                    </div>
                  </div>
                </div>
              </ConfigSection>

              {/* Typography */}
              <ConfigSection
                title="Typography"
                icon={Type}
                id="typography"
                isExpanded={expandedSection === "typography"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <SelectField
                      label="Heading Font"
                      name="typography.headingFont"
                      value={config.typography?.headingFont}
                      onChange={handleFieldChange}
                      options={[
                        { value: "Inter", label: "Inter" },
                        { value: "Roboto", label: "Roboto" },
                        { value: "Open Sans", label: "Open Sans" },
                        { value: "Montserrat", label: "Montserrat" },
                        { value: "Lato", label: "Lato" },
                        { value: "Poppins", label: "Poppins" },
                        {
                          value: "Playfair Display",
                          label: "Playfair Display",
                        },
                        { value: "Merriweather", label: "Merriweather" },
                      ]}
                    />

                    <SelectField
                      label="Body Font"
                      name="typography.bodyFont"
                      value={config.typography?.bodyFont}
                      onChange={handleFieldChange}
                      options={[
                        { value: "Inter", label: "Inter" },
                        { value: "Roboto", label: "Roboto" },
                        { value: "Open Sans", label: "Open Sans" },
                        { value: "Montserrat", label: "Montserrat" },
                        { value: "Lato", label: "Lato" },
                        { value: "Poppins", label: "Poppins" },
                        { value: "Source Sans Pro", label: "Source Sans Pro" },
                        { value: "Nunito", label: "Nunito" },
                      ]}
                    />
                  </div>

                  <div className="space-y-4">
                    <SelectField
                      label="Base Font Size"
                      name="typography.baseSize"
                      value={config.typography?.baseSize}
                      onChange={handleFieldChange}
                      options={[
                        { value: "14px", label: "Small (14px)" },
                        { value: "16px", label: "Medium (16px)" },
                        { value: "18px", label: "Large (18px)" },
                      ]}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Typography Preview
                  </h4>
                  <div
                    className="p-6 border border-gray-200 rounded-md"
                    style={{
                      fontFamily: `${config.typography?.bodyFont}, system-ui, sans-serif`,
                      fontSize: config.typography?.baseSize,
                    }}
                  >
                    <h1
                      className="text-3xl mb-3"
                      style={{
                        fontFamily: `${config.typography?.headingFont}, system-ui, sans-serif`,
                        color: config.colors?.textPrimary,
                      }}
                    >
                      Heading Font: {config.typography?.headingFont}
                    </h1>
                    <p
                      className="mb-4"
                      style={{ color: config.colors?.textPrimary }}
                    >
                      Body Font: {config.typography?.bodyFont} at{" "}
                      {config.typography?.baseSize} size. This is a preview of
                      how text will appear on your website using your selected
                      typography settings.
                    </p>
                    <p style={{ color: config.colors?.textSecondary }}>
                      This is secondary text using your selected colors and
                      fonts.
                    </p>
                  </div>
                </div>
              </ConfigSection>

              {/* SEO & Meta */}
              <ConfigSection
                title="SEO & Meta"
                icon={Info}
                id="seo"
                isExpanded={expandedSection === "seo"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Default Meta Title"
                      name="metaTitle"
                      value={config.metaTitle}
                      onChange={handleFieldChange}
                      helperText="Defaults to Site Name if empty"
                    />

                    <TextArea
                      label="Default Meta Description"
                      name="metaDescription"
                      value={config.metaDescription}
                      onChange={handleFieldChange}
                      helperText="Defaults to Site Description if empty"
                    />
                  </div>

                  <div className="space-y-4">
                    <TextField
                      label="Google Analytics ID"
                      name="googleAnalyticsId"
                      value={config.googleAnalyticsId}
                      onChange={handleFieldChange}
                      placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                      helperText="Your Google Analytics tracking ID"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* Feature Toggles */}
              <ConfigSection
                title="Feature Toggles"
                icon={Settings}
                id="features"
                isExpanded={expandedSection === "features"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Checkbox
                      label="Enable Blog"
                      name="features.enableBlog"
                      checked={config.features?.enableBlog}
                      onChange={handleFieldChange}
                      helperText="Show blog posts section on website"
                    />

                    <Checkbox
                      label="Enable Newsletter"
                      name="features.enableNewsletter"
                      checked={config.features?.enableNewsletter}
                      onChange={handleFieldChange}
                      helperText="Show newsletter signup forms"
                    />
                  </div>

                  <div>
                    <Checkbox
                      label="Enable Search"
                      name="features.enableSearch"
                      checked={config.features?.enableSearch}
                      onChange={handleFieldChange}
                      helperText="Show search bar in header"
                    />

                    <Checkbox
                      label="Enable Dark Mode"
                      name="features.enableDarkMode"
                      checked={config.features?.enableDarkMode}
                      onChange={handleFieldChange}
                      helperText="Allow users to toggle between light and dark themes"
                    />
                  </div>
                </div>
              </ConfigSection>

              {/* API Keys & Integrations */}
              <ConfigSection
                title="API Keys & Integrations"
                icon={Layout}
                id="apiKeys"
                isExpanded={expandedSection === "apiKeys"}
                onToggle={toggleSection}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <TextField
                      label="Google Maps API Key"
                      name="apiKeys.googleMaps"
                      value={config.apiKeys?.googleMaps}
                      onChange={handleFieldChange}
                      helperText="Required for maps functionality"
                    />

                    <TextField
                      label="Mailchimp API Key"
                      name="apiKeys.mailchimp"
                      value={config.apiKeys?.mailchimp}
                      onChange={handleFieldChange}
                      helperText="Required for newsletter functionality"
                    />
                  </div>

                  <div className="space-y-4">
                    <TextField
                      label="Stripe API Key"
                      name="apiKeys.stripe"
                      value={config.apiKeys?.stripe}
                      onChange={handleFieldChange}
                      helperText="Required for payment processing"
                    />
                  </div>
                </div>

                <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-md">
                  <div className="flex">
                    <Info className="h-5 w-5 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Security Notice</p>
                      <p className="text-xs mt-1">
                        API keys are sensitive information. Ensure your server
                        environment correctly handles these credentials
                        securely.
                      </p>
                    </div>
                  </div>
                </div>
              </ConfigSection>
            </div>

            <button
              onClick={async () => {
                if (
                  window.confirm(
                    "Are you sure you want to migrate all images from MongoDB to the file system? This process cannot be undone and may take several minutes."
                  )
                ) {
                  try {
                    console.log({
                      type: "info",
                      message:
                        "Migration started. This may take several minutes...",
                    });

                    const response = await fetch("/api/admin/migrate-images", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                          "token"
                        )}`,
                      },
                    });

                    const data = await response.json();

                    if (response.ok) {
                      console.log({
                        type: "success",
                        message:
                          "Image migration completed successfully! Please refresh the page to see changes.",
                      });
                    } else {
                      throw new Error(data.error || "Migration failed");
                    }
                  } catch (error) {
                    console.error("Migration error:", error);
                    console.log({
                      type: "error",
                      message: `Migration failed: ${error.message}`,
                    });
                  }
                }
              }}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-200"
            >
              Migrate Images from MongoDB to File System
            </button>

            <button
              onClick={async () => {
                if (
                  window.confirm(
                    "This will update your database to use the filesystem image paths. This operation is safe to run multiple times. Continue?"
                  )
                ) {
                  try {
                    console.log({
                      type: "info",
                      message:
                        "Database update started. This may take a few minutes...",
                    });

                    const response = await fetch(
                      "/api/admin/update-image-references",
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${localStorage.getItem(
                            "token"
                          )}`,
                        },
                      }
                    );

                    const data = await response.json();

                    if (response.ok) {
                      console.log({
                        type: "success",
                        message: `Database updated successfully! Products: ${data.stats.productsUpdated}, Categories: ${data.stats.categoriesUpdated}, Parent Categories: ${data.stats.parentCategoriesUpdated}`,
                      });
                    } else {
                      throw new Error(data.error || "Update failed");
                    }
                  } catch (error) {
                    console.error("Database update error:", error);
                    console.log({
                      type: "error",
                      message: `Update failed: ${error.message}`,
                    });
                  }
                }
              }}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-200"
            >
              Update Database Image References
            </button>
            <p>
              {" "}
              how it works:
              <br />
              first comment the new model in product, categry & parent category
              to include the img id in each model (using old implemenation).
              <br />
              then, press the migrate img from mongo to file system to do the
              trasfer. <br />
              last, comment the old img id filed in product model, parent and
              category to let system save new img data in each model & press
              update database
            </p>

            {/* Save Button */}
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving Configuration...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save All Settings
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConfigManagement;
